/**
 * AdminKitGallery — the P0 kit's demo harness (the repo has no Storybook).
 * Renders every kit component with fixtures so reviewers can eyeball the
 * design language and so the jest-axe spec has a single screen to assert. It is
 * a development/demo surface (gated by ops.metrics.read like the dashboard) and
 * is intentionally NOT linked from the admin nav. Design: _shared/KIT-SPEC.md.
 */
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import {
  AdminDataTable,
  BudgetGauge,
  DistributionChart,
  FilterToolbar,
  KpiStatCard,
  SplitChart,
  TrendChart,
  type DataTableColumn,
  type SortState,
} from "@/features/admin/_shared";

interface DemoRow {
  id: string;
  name: string;
  count: number;
}
const DEMO_ROWS: DemoRow[] = [
  { id: "1", name: "Цемент М400", count: 128 },
  { id: "2", name: "Гипсокартон", count: 64 },
  { id: "3", name: "Ламинат дуб", count: 31 },
];
const DEMO_COLUMNS: DataTableColumn[] = [
  { key: "name", label: "Товар", sortable: true },
  { key: "count", label: "Кол-во", align: "right", sortable: true },
];
const TREND = [
  { x: "Пн", y: 12 },
  { x: "Вт", y: 19 },
  { x: "Ср", y: 8 },
  { x: "Чт", y: 23 },
  { x: "Пт", y: 17 },
];
const FUNNEL = [
  { label: "Подано", value: 80, tone: "primary" as const },
  { label: "На проверке", value: 42, tone: "warn" as const },
  { label: "Одобрено", value: 28, tone: "success" as const },
];
const SPLIT = [
  { label: "Активные", value: 70 },
  { label: "На модерации", value: 18 },
  { label: "Скрытые", value: 12 },
];

export function AdminKitGallery(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const [sort, setSort] = useState<SortState>({ key: "count", direction: "desc" });
  const [search, setSearch] = useState("");
  const [chip, setChip] = useState<string | null>(null);

  const sl = {
    loadingLabel: t("kit.states.loading"),
    emptyLabel: t("kit.chart.noData"),
    errorLabel: t("kit.states.error"),
  };

  return (
    <div data-testid="admin-kit-gallery" className="mx-auto flex max-w-5xl flex-col gap-8">
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-2xl font-bold tracking-tight text-foreground">{t("kit.gallery.title")}</h1>
        <p className="text-sm text-[var(--g-secondary)]">{t("kit.gallery.subtitle")}</p>
      </header>

      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <KpiStatCard
          testid="gallery-kpi"
          label="Регистрации"
          value="1 240"
          delta={{ value: 12, direction: "up", goodDirection: "up", label: t("kit.kpi.up") }}
          sparkline={[4, 8, 6, 12, 9, 14]}
          status={{ tone: "success", label: t("kit.budget.under") }}
        />
        <div data-testid="gallery-budget" className="rounded-[var(--g-radius)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-4">
          <BudgetGauge
            testid="gallery-budget-gauge"
            spent={2450}
            ceiling={3000}
            label="Дневной AI-бюджет"
            statusLabels={{ under: t("kit.budget.under"), approaching: t("kit.budget.approaching"), over: t("kit.budget.over") }}
            remainingLabel={t("kit.budget.remaining")}
            formatValue={(n) => n.toLocaleString("ru-RU")}
          />
        </div>
        <div data-testid="gallery-split" className="rounded-[var(--g-radius)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-4">
          <SplitChart data={SPLIT} ariaLabel="Статусы товаров" centerLabel="всего" centerValue="100" {...sl} />
        </div>
      </section>

      <section data-testid="gallery-toolbar">
        <FilterToolbar
          testid="gallery-filter"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={t("kit.filter.searchPlaceholder")}
          searchAriaLabel={t("common.search")}
          chips={[
            { id: "active", label: "Активные" },
            { id: "pending", label: "На модерации" },
          ]}
          activeChipId={chip}
          onChipChange={setChip}
          allChipLabel={t("kit.filter.all")}
        />
      </section>

      <section data-testid="gallery-table">
        <AdminDataTable<DemoRow>
          columns={DEMO_COLUMNS}
          rows={DEMO_ROWS}
          rowKey={(r) => r.id}
          renderCell={(r, k) => (k === "name" ? r.name : String(r.count))}
          renderMobileCard={(r) => <span>{r.name}</span>}
          rowAriaLabel={(r) => r.name}
          testid="gallery-data-table"
          isLoading={false}
          isError={false}
          emptyLabel={t("kit.states.empty")}
          errorLabel={t("kit.states.error")}
          sort={sort}
          onSortChange={setSort}
          sortAscLabel={t("kit.table.sortAsc")}
          sortDescLabel={t("kit.table.sortDesc")}
        />
      </section>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div data-testid="gallery-trend" className="rounded-[var(--g-radius)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-4">
          <TrendChart data={TREND} mode="area" ariaLabel="Регистрации за неделю" {...sl} />
        </div>
        <div data-testid="gallery-distribution" className="rounded-[var(--g-radius)] border border-[var(--g-hairline)] bg-[var(--g-surface)] p-4">
          <DistributionChart data={FUNNEL} ariaLabel="Воронка заявок продавцов" {...sl} />
        </div>
      </section>
    </div>
  );
}

export default AdminKitGallery;
